/* Proprietary - see LICENSE */
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  LayoutDashboard, 
  FileCheck, 
  ListTodo, 
  AlertTriangle, 
  HardDrive,
  Cpu, 
  AlertCircle, 
  FileText, 
  Sparkles,
  Folder,
  Bell,
  Users,
  Menu,
  X,
  LogOut,
  User,
  Settings,
  ChevronDown,
  Building2
} from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import { useHasFramework, useCurrentTenant } from '../hooks/useApi';
import LanguageSwitcher from './LanguageSwitcher';
import apiClient from '../config/apiClient';
import './Layout.css';

const Layout = ({ children, setAuth }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [tenantMenuOpen, setTenantMenuOpen] = useState(false);
  const [accessibleTenants, setAccessibleTenants] = useState([]);
  const [currentTenantName, setCurrentTenantName] = useState('');
  const { canManageUsers } = usePermissions();
  // Framework-specific views appear with their framework and not before.
  const hasAiAct = useHasFramework('AI_ACT');
  // The organisation's own logo heads the sidebar, so the platform reads as
  // theirs rather than as a product they are logged into. Falls back to the
  // INCO mark when a tenant has not uploaded one.
  const { data: currentTenant } = useCurrentTenant();
  const orgLogo = currentTenant?.logo || null;
  const userMenuRef = useRef(null);
  const tenantMenuRef = useRef(null);

  useEffect(() => {
    // Load current user from session storage
    const userStr = sessionStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (tenantMenuRef.current && !tenantMenuRef.current.contains(event.target)) {
        setTenantMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load accessible tenants for the switcher
  const showTenantSwitcher = currentUser && (
    ['super_admin', 'owner', 'provider_admin'].includes(currentUser.role) || currentUser.multiTenant
  );

  useEffect(() => {
    if (!showTenantSwitcher) return;
    let cancelled = false;
    const loadTenants = async () => {
      try {
        const res = await apiClient.get('/auth/accessible-tenants');
        if (!cancelled) {
          const list = Array.isArray(res.data) ? res.data : [];
          setAccessibleTenants(list);
          const selectedId = sessionStorage.getItem('selectedTenantId');
          const found = list.find((t) => t.id === selectedId);
          setCurrentTenantName(found?.name || '');
        }
      } catch { /* ignore */ }
    };
    loadTenants();
    return () => { cancelled = true; };
  }, [showTenantSwitcher]);

  const handleSwitchTenant = (tenantId) => {
    sessionStorage.setItem('selectedTenantId', tenantId);
    try {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      user.tenantId = tenantId;
      sessionStorage.setItem('user', JSON.stringify(user));
    } catch { /* ignore */ }
    const found = accessibleTenants.find((t) => t.id === tenantId);
    setCurrentTenantName(found?.name || '');
    setTenantMenuOpen(false);
    window.dispatchEvent(new Event('tenant:selected'));
    window.location.reload();
  };

  const getUserInitials = () => {
    if (!currentUser) return 'U';
    const first = currentUser.firstName?.charAt(0) || '';
    const last = currentUser.lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  const getUserFullName = () => {
    if (!currentUser) return 'User';
    return `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || 'User';
  };

  const getUserRole = () => {
    if (!currentUser) return '';
    const roleLabels = {
      super_admin: t('users.superAdmin'),
      owner: t('users.owner'),
      admin: t('users.admin'),
      manager: t('users.manager'),
      compliance_manager: t('users.manager'),
      user: t('users.user'),
      viewer: t('users.user')
    };
    return roleLabels[currentUser.role] || currentUser.role;
  };

  const canAccessWhistleblower = !!currentUser;

  /**
   * Initials for the collapsed sidebar.
   *
   * A logo is usually a wordmark — the one in use is nearly five times as wide
   * as it is tall — and 30px of collapsed rail turns that into an illegible
   * sliver. Initials stay readable at any size and are still the organisation's
   * own, which the INCO shield would not be.
   */
  const getOrgInitials = () => {
    const name = currentTenant?.name || '';
    // Oy, Ab and the like say nothing about which company this is.
    const suffixes = new Set(['oy', 'oyj', 'ab', 'ltd', 'inc', 'gmbh', 'as', 'a/s']);
    const words = name
      .split(/\s+/)
      .filter((w) => w && !suffixes.has(w.toLowerCase().replace(/[.,]/g, '')));

    if (words.length === 0) return '';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const navigation = [
    { name: t('nav.dashboard'), path: '/', icon: LayoutDashboard },
    { name: t('nav.frameworks'), path: '/frameworks', icon: FileCheck },
    { name: t('nav.tasks'), path: '/tasks', icon: ListTodo },
    // Documents sits above the registers it describes, and risk above the
    // incidents it anticipates — the order follows how the work is done
    // rather than the order the features happened to be built in.
    { name: t('nav.documents'), path: '/documents', icon: Folder },
    { name: t('nav.assets'), path: '/assets', icon: HardDrive },
    ...(hasAiAct
      ? [{ name: t('nav.aiSystems', 'AI-järjestelmät'), path: '/ai-systems', icon: Cpu }]
      : []),
    { name: t('nav.riskManagement'), path: '/risks', icon: AlertTriangle },
    { name: t('nav.incidents'), path: '/incidents', icon: AlertCircle },
    ...(canAccessWhistleblower ? [{ name: t('nav.whistleblower'), path: '/whistleblower', icon: Bell }] : []),
    { name: t('nav.reports'), path: '/reports', icon: FileText },
    { name: t('nav.aiAssistant'), path: '/ai-assistant', icon: Sparkles },
    ...(canManageUsers ? [{ name: t('nav.users'), path: '/users', icon: Users }] : []),
  ];

  const handleLogout = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('selectedTenantId');
    setAuth(false);
  };

  return (
    <div className="layout">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="logo">
            {/* An organisation with a name of only "Oy" leaves nothing to make
                initials from, so that case falls through to the INCO mark
                rather than showing an empty square. */}
            {orgLogo && sidebarOpen ? (
              // An organisation's logo keeps its own colours — the white-out
              // filter below is for the monochrome INCO mark, and would turn
              // someone's brand into a silhouette. A light plate carries it
              // instead, so a dark logo stays readable on the dark sidebar.
              <img
                className="org-logo"
                src={orgLogo}
                alt={currentTenant?.name || ''}
              />
            ) : orgLogo && getOrgInitials() ? (
              <span className="org-mark" title={currentTenant?.name || ''}>
                {getOrgInitials()}
              </span>
            ) : sidebarOpen ? (
              <img className="logo-image" src="/images/inco-logo4.png" alt="INCO" />
            ) : (
              <img className="logo-image-collapsed" src="/images/inco-shield.png" alt="INCO" />
            )}
          </div>
          <button 
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive ? 'active' : ''}`}
              >
                <Icon size={20} />
                {sidebarOpen && <span>{item.name}</span>}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <header className="top-bar">
          <div className="breadcrumb">
            {location.pathname === '/settings'
              ? t('nav.settings')
              : navigation.find(item => item.path === location.pathname)?.name || t('nav.dashboard')}
          </div>
          <div className="user-info">
            <LanguageSwitcher />
            {showTenantSwitcher && accessibleTenants.length > 1 && (
              <div className="tenant-switcher-container" ref={tenantMenuRef}>
                <button
                  className="tenant-switcher-trigger"
                  onClick={() => setTenantMenuOpen(!tenantMenuOpen)}
                  title={t('tenantSwitcher.switchOrg', 'Switch organization')}
                >
                  {/* No logo here: the current organisation's logo now heads
                      the sidebar, and repeating it in the trigger showed the
                      same thing twice. The list below keeps its logos, where
                      they do a different job — telling the options apart. */}
                  <Building2 size={18} />
                  <span className="tenant-switcher-name">{currentTenantName || t('tenantSwitcher.selectOrg', 'Select organization')}</span>
                  <ChevronDown size={14} className={`chevron ${tenantMenuOpen ? 'open' : ''}`} />
                </button>
                {tenantMenuOpen && (
                  <div className="tenant-switcher-dropdown">
                    <div className="tenant-switcher-header">{t('tenantSwitcher.switchOrg', 'Switch organization')}</div>
                    <div className="tenant-switcher-list">
                      {accessibleTenants.map((tenant) => (
                        <button
                          key={tenant.id}
                          className={`tenant-switcher-item ${tenant.id === sessionStorage.getItem('selectedTenantId') ? 'active' : ''}`}
                          onClick={() => handleSwitchTenant(tenant.id)}
                        >
                          {tenant.logo ? (
                            <img
                              className="tenant-switcher-logo"
                              src={tenant.logo}
                              alt=""
                            />
                          ) : (
                            <Building2 size={16} />
                          )}
                          <span>{tenant.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="user-menu-container" ref={userMenuRef}>
              <button 
                className="user-menu-trigger"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                <div className="user-avatar">{getUserInitials()}</div>
                <ChevronDown size={16} className={`chevron ${userMenuOpen ? 'open' : ''}`} />
              </button>
              
              {userMenuOpen && (
                <div className="user-menu-dropdown">
                  <div className="user-menu-header">
                    <div className="user-menu-avatar">{getUserInitials()}</div>
                    <div className="user-menu-info">
                      <div className="user-menu-name">{getUserFullName()}</div>
                      <div className="user-menu-role">{getUserRole()}</div>
                      <div className="user-menu-email">{currentUser?.email}</div>
                    </div>
                  </div>
                  
                  <div className="user-menu-divider"></div>
                  
                  <div className="user-menu-items">
                    <button className="user-menu-item" onClick={() => {
                      setUserMenuOpen(false);
                      navigate('/settings');
                    }}>
                      <Settings size={18} />
                      <span>{t('nav.settings')}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="content">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
